import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@common/Button';
import { Toast } from '@common/Toast';
import { logError, logInfo } from '@utils/logger';

// Create form context
const FormContext = React.createContext(null);

const BasePOIModal = ({ type, poiInfo = {}, onClose, onSave, onBack, children }) => {
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  // Form context value
  const formContextValue = {
    formData,
    formErrors,
    onChange: (field, value) => setFormData(prev => ({ ...prev, [field]: value })),
    onError: (field, error) => setFormErrors(prev => ({ ...prev, [field]: error }))
  };
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFormError = (field, error) => {
    setFormErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // Ensure we have valid coordinates
      if (!poiInfo.coordinates?.latitude || !poiInfo.coordinates?.longitude) {
        throw new Error('Location coordinates are required');
      }

      // Prepare POI data
      const poiData = {
        type,
        coordinates: {
          latitude: Number(poiInfo.coordinates.latitude),
          longitude: Number(poiInfo.coordinates.longitude)
        },
        ...formData
      };

      await onSave(poiData);
      logInfo('High pole measurement saved successfully');

      setToast({
        type: 'success',
        message: 'High pole measurement saved successfully',
        duration: 2000
      });
      
      onClose();
    } catch (error) {
      logError('Failed to save high pole measurement:', error);
      setError(error.message || 'Failed to save measurement');
      setToast({
        type: 'error',
        message: error.message || 'Failed to save measurement',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-[400] p-4 overflow-y-auto">
      <div className="bg-[#2A2B2E] rounded-lg w-full max-w-xl my-8" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="border-b border-gray-700 p-6">
          <div className="flex items-start justify-between">
            <div className="space-y-3">
              <h2 className="text-xl font-semibold text-white">
                High Pole Measurement - {type}
              </h2>
              <div className="space-y-1.5">
                <div className="font-mono text-sm text-blue-400 mb-1">
                  POI ID: {poiInfo.id}
                </div>
                {poiInfo.coordinates && (
                  <div className="text-sm text-gray-400">
                    GPS: {Number(poiInfo.coordinates.latitude).toFixed(6)}, {Number(poiInfo.coordinates.longitude).toFixed(6)}
                  </div>
                )}
                <div className="text-sm text-gray-400">
                  Address: {poiInfo.address || 'Getting address...'}
                </div>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <FormContext.Provider value={formContextValue}>
            <div className="space-y-6">
              {error && (
                <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
                  {error}
                </div>
              )}

              {typeof children === 'function' 
                ? children(formData, formContextValue.onChange)
                : children
              }
            </div>
          </FormContext.Provider>

          {/* Validation Errors */}
          {Object.keys(formErrors).length > 0 && (
            <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
              <ul className="list-disc list-inside">
                {Object.entries(formErrors).map(([field, error]) => (
                  <li key={field}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </form>

        {/* Footer */}
        <footer className="border-t border-gray-700 p-6">
          <div className="flex justify-end gap-3">
            {onBack && (
              <Button
                onClick={onBack}
                variant="secondary"
                className="bg-gray-700 hover:bg-gray-600 text-white"
              >
                Back
              </Button>
            )}
            <Button
              onClick={onClose}
              variant="secondary"
              className="bg-gray-700 hover:bg-gray-600 text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={loading}
              disabled={loading || Object.keys(formErrors).length > 0}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              Save Measurement
            </Button>
          </div>
        </footer>
      </div>
      
      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

// Export form context hook
export const useFormContext = () => {
  const context = React.useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a BasePOIModal');
  }
  return context;
};

BasePOIModal.displayName = 'BasePOIModal';

export default BasePOIModal;